import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Activity, ScanLine } from "lucide-react";
import { SITE, whatsappLink } from "@/lib/site";

const stats = [
  { value: "10+", label: "Years Experience" },
  { value: "500+", label: "Clients Trained" },
  { value: "Dubai", label: "Premium Location" },
];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#050505] grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={SITE.images.hero}
          alt="Personal trainer Anoop Gopi coaching a client at a Dubai gym"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover opacity-55 sm:opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/55 to-black"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent"></div>
      </div>

      {/* Aurora */}
      <div className="aurora" style={{ background: "#0055FF", width: 500, height: 500, top: -120, left: -120 }} />
      <div className="aurora" style={{ background: "#00E5FF", width: 420, height: 420, bottom: -120, right: -100, opacity: 0.25 }} />

      {/* Grid lines */}
      <div className="absolute inset-0 grid-lines opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-28 sm:pt-36 pb-20 sm:pb-24 lg:pt-44">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mb-6 sm:mb-7"
              data-testid="hero-wordmark"
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <span className="h-px w-10 sm:w-14 bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-[#00E5FF]"></span>
                <span className="font-display font-light text-white text-xl sm:text-2xl lg:text-3xl tracking-[0.32em] uppercase">
                  Corrective <span className="text-gradient-blue font-medium">Motion</span>
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full glass text-[10px] sm:text-xs uppercase tracking-[0.2em] text-zinc-300 mb-6 sm:mb-8"
              data-testid="hero-badge"
            >
              <Activity className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#00E5FF]" />
              Evidence-Based · Dubai
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-[2.5rem] leading-[1.02] sm:text-5xl sm:leading-[1.02] md:text-6xl lg:text-7xl xl:text-[80px] xl:leading-[0.98] font-extrabold tracking-[-0.03em] text-white text-balance"
              data-testid="hero-headline"
            >
              <span className="block">Personal Trainer &amp; Corrective Exercise Specialist in Dubai</span>
              <span className="block text-gradient-blue mt-3 sm:mt-4">Move Better. Build Strength. Live With Less Pain.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-6 sm:mt-7 max-w-xl text-base sm:text-lg text-zinc-300/90 leading-relaxed"
              data-testid="hero-subheadline"
            >
              Evidence-based corrective training that improves movement, reduces pain,
              builds long-term strength, and supports sustainable weight loss — without
              compromising joint health.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
            >
              <a
                href="#consult"
                data-testid="hero-primary-cta"
                className="group inline-flex justify-center items-center gap-2 bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black px-6 sm:px-7 py-3.5 sm:py-4 rounded-full font-semibold tracking-wide hover:shadow-[0_0_40px_-5px_rgba(0,229,255,0.55)] transition-shadow"
              >
                Start Your Transformation
                <ArrowUpRight className="w-5 h-5 transition-transform group-hover:rotate-45" />
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex justify-center items-center gap-2 px-6 sm:px-7 py-3.5 sm:py-4 rounded-full border border-white/15 text-white font-semibold tracking-wide hover:border-[#00E5FF]/50 hover:bg-white/5 transition-colors"
              >
                <MessageCircle className="w-5 h-5 text-[#25D366]" /> Chat on WhatsApp
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-12 sm:mt-14 grid grid-cols-3 max-w-md gap-4 sm:gap-6"
              data-testid="hero-stats"
            >
              {stats.map((s) => (
                <div key={s.label} className="border-l border-white/10 pl-3 sm:pl-4">
                  <div className="font-display text-2xl sm:text-3xl font-bold text-white">{s.value}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider text-zinc-400 mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right floating card – Movement & Posture Analysis */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="lg:col-span-5 hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-[#0055FF]/30 to-[#00E5FF]/30 blur-2xl"></div>
              <div className="relative glass rounded-3xl p-2 ring-glow">
                <img
                  src={SITE.images.assessment}
                  alt="Movement & posture analysis with Anoop Gopi"
                  decoding="async"
                  className="rounded-2xl w-full h-[520px] object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">Signature Service</div>
                    <div className="text-white font-display font-semibold text-lg mt-1 leading-tight">
                      Movement &amp; Posture Analysis
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-gradient-cm flex items-center justify-center shrink-0">
                    <ScanLine className="w-5 h-5 text-black" strokeWidth={2.5} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-black pointer-events-none"></div>
    </section>
  );
};

export default Hero;
